import React, { useState } from "react";
import { observer } from "mobx-react";
import { Box, Grid2, Paper, useTheme } from "@mui/material";
import { useStores } from "../../../use-stores";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import PermissionBlockLabel from "../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import LoaderOverlay from "../../../../../views/components/common/pages/AppLoader";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import {
  dailyTasksFamilyLabel,
} from "../../../../../models/Dashboard/DailyTasks/DailyTasks";
import { DailyTasksStore } from "../../../stores/dashboards/DailyTasksStore";
import DailyTasksTatFilters from "../../components/dashboard/daily-tasks/DailyTasksTatFilters";
import DailyTasksActivity from "../../components/dashboard/daily-tasks/DailyTasksActivity";
import DailyTasksDaysLedger from "../../components/dashboard/daily-tasks/DailyTasksDaysLedger";
import DailyTasksStageGrid from "../../components/dashboard/daily-tasks/DailyTasksStageGrid";
import DailyTasksWorkQueue from "../../components/dashboard/daily-tasks/DailyTasksWorkQueue";
import DailyTasksDrilldownDrawer from "../../components/dashboard/daily-tasks/DailyTasksDrilldownDrawer";
import DailyTasksDrawer from "../../components/dashboard/daily-tasks/DailyTasksDrawer";

const DailyTasksFamilyPlaceholder: React.FC<{ store: DailyTasksStore }> = observer(
  ({ store }) => {
    const theme = useTheme();
    const family = dailyTasksFamilyLabel(store.filters.stage);
    return (
      <Paper
        sx={{
          mt: 3,
          p: { xs: 2, md: 3 },
          borderRadius: 2,
          boxShadow:
            "rgba(0, 0, 0, 0.15) 0px 4px 24px, rgba(0, 0, 0, 0.05) 0px 8px 32px",
        }}
      >
        <CustomTypography variant="heading3" sx={{ mb: 0.5 }}>
          {family}
        </CustomTypography>
        <CustomTypography
          variant="body2"
          color={theme.palette.customColors.greyTextColor}
          sx={{ mb: 2 }}
        >
          Same Tasks layout: cycle time, stage times, then activity for
          this {family} stage.
        </CustomTypography>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="10rem"
          textAlign="center"
          border="1px dashed"
          borderColor="divider"
          borderRadius={2}
          px={2}
        >
          <CustomTypography
            variant="heading6"
            fontWeight={500}
            color={theme.palette.customColors.greyTextColor}
          >
            {family} turnaround is not live yet. Switch back to Mockup to see
            current data.
          </CustomTypography>
        </Box>
      </Paper>
    );
  },
);

const DailyTasksDashboardView: React.FC = observer(() => {
  const [store] = useState(() => new DailyTasksStore());
  const { userStore } = useStores();

  if (!userStore.hasPermission(EmployeeRoles.VIEW_TASKS_DASHBOARD)) {
    return <PermissionBlockLabel />;
  }

  return (
    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }} sx={{ px: 2 }}>
      <LoaderOverlay loading={store.summaryLoading || store.filtersLoading} />
      <DailyTasksTatFilters store={store} />
      {store.isFamilyReady ? (
        <>
          <DailyTasksDaysLedger store={store} />
          <DailyTasksStageGrid store={store} />
          <DailyTasksActivity store={store} />
          <DailyTasksWorkQueue store={store} />
          <DailyTasksDrilldownDrawer store={store} />
          <DailyTasksDrawer store={store} />
        </>
      ) : (
        <DailyTasksFamilyPlaceholder store={store} />
      )}
    </Grid2>
  );
});

const DailyTasksDashboard: React.FC = () => {
  const { userStore } = useStores();

  if (!userStore.hasPermission(EmployeeRoles.VIEW_TASKS_DASHBOARD)) {
    return <PermissionBlockLabel />;
  }

  return <DailyTasksDashboardView />;
};

export default observer(DailyTasksDashboard);
